// src/components/AcknowledgmentPanel.tsx
// Panel showing acknowledgment status for a document.

import { useMemo } from 'react';
import { useI18n } from '@react/hooks';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from '@react/components';

import { formatDate, getInitials, getAvatarColor } from '@/features/document/logic';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface AcknowledgmentPanelProps {
  documentId: string;
  acknowledgments: any[];
  total: number;
  onSendReminder: (userId: string) => void;
  onRequestAcknowledgment: () => void;
  isLoading?: boolean;
}

// ---------------------------------------------------------------------------
// Progress bar
// ---------------------------------------------------------------------------

function ProgressBar({ percentage }: { percentage: number }) {
  return (
    <div className="w-full h-2 rounded-full bg-muted overflow-hidden">
      <div
        className="h-full rounded-full bg-primary transition-all duration-300"
        style={{ width: `${Math.min(100, Math.max(0, percentage))}%` }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Skeleton
// ---------------------------------------------------------------------------

function AcknowledgmentSkeleton() {
  return (
    <Card>
      <CardHeader>
        <div className="h-5 w-40 rounded bg-muted animate-pulse" />
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="h-2 w-full rounded-full bg-muted animate-pulse" />
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3 animate-pulse">
              <div className="h-8 w-8 rounded-full bg-muted" />
              <div className="flex-1">
                <div className="h-4 w-24 rounded bg-muted" />
              </div>
              <div className="h-5 w-20 rounded-full bg-muted" />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AcknowledgmentPanel({
  documentId: _documentId,
  acknowledgments,
  total,
  onSendReminder,
  onRequestAcknowledgment,
  isLoading,
}: AcknowledgmentPanelProps) {
  const { t, locale } = useI18n();

  const stats = useMemo(() => {
    const acknowledged = acknowledgments.filter((a: any) => a?.status === 'acknowledged').length;
    const percentage = total > 0 ? Math.round((acknowledged / total) * 100) : 0;
    return { acknowledged, percentage };
  }, [acknowledgments, total]);

  if (isLoading) {
    return <AcknowledgmentSkeleton />;
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold">
            {t('document.acknowledgment.title')}
          </CardTitle>
          <Button size="sm" onClick={onRequestAcknowledgment}>
            {t('document.acknowledgment.request')}
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Progress */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>
              {t('document.acknowledgment.progress', {
                acknowledged: stats.acknowledged.toString(),
                total: total.toString(),
              })}
            </span>
            <span>{stats.percentage}%</span>
          </div>
          <ProgressBar percentage={stats.percentage} />
        </div>

        {/* People list */}
        {acknowledgments.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            {t('document.acknowledgment.noAcknowledgments')}
          </p>
        ) : (
          <div className="space-y-2">
            {acknowledgments.map((ack: any) => {
              const userId = ack?.user_id || ack?.user?.id || '';
              const name = ack?.user?.display_name || ack?.user?.name || ack?.name || t('common.unknown');
              const isAcknowledged = ack?.status === 'acknowledged';
              const acknowledgedAt = ack?.acknowledged_at;

              return (
                <div
                  key={userId || name}
                  className="flex items-center gap-3 py-1.5"
                >
                  {/* Avatar */}
                  <div
                    className="h-8 w-8 rounded-full flex items-center justify-center text-xs font-semibold text-white flex-shrink-0"
                    style={{ backgroundColor: getAvatarColor(name) }}
                  >
                    {getInitials(name)}
                  </div>

                  {/* Name */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-foreground truncate">{name}</p>
                    {isAcknowledged && acknowledgedAt && (
                      <p className="text-xs text-muted-foreground">
                        {formatDate(acknowledgedAt, locale)}
                      </p>
                    )}
                  </div>

                  {/* Status */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {isAcknowledged ? (
                      <Badge variant="success" size="sm">
                        {t('document.acknowledgment.acknowledged')}
                      </Badge>
                    ) : (
                      <>
                        <Badge variant="secondary" size="sm">
                          {t('document.acknowledgment.pending')}
                        </Badge>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => onSendReminder(userId)}
                        >
                          {t('document.acknowledgment.remind')}
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default AcknowledgmentPanel;
